import { useAuth } from "@/components/auth/AuthProvider";
import { useTeams } from "./useTeams";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";
import { useState } from "react";

export const useStripePayment = () => {
  const { user } = useAuth();
  const { teams } = useTeams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Creates a Stripe checkout session via the edge function
   * @param teamId - Team ID to subscribe
   * @param planId - Optional plan ID (defaults to first active broker_platform plan)
   */
  const createCheckoutSession = async (teamId?: string, planId?: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to continue",
        variant: "destructive",
      });
      return null;
    }

    const finalTeamId = teamId || teams[0]?.teams?.id;

    if (!finalTeamId) {
      toast({
        title: "No team found",
        description: "Please create a team first",
        variant: "destructive",
      });
      return null;
    }

    setIsLoading(true);

    try {
      console.log('Creating checkout session for team:', finalTeamId, 'plan:', planId);

      const { data, error } = await supabase.functions.invoke('create-team-checkout', {
        body: {
          teamId: finalTeamId,
          planId: planId || undefined  // Let backend choose default plan
        }
      });

      if (error) {
        console.error('Checkout creation error:', error);
        throw error;
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      console.log('Checkout session created:', data.url);
      return data.url;

    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: "Checkout failed",
        description: error instanceof Error ? error.message : "Failed to create checkout session",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Opens Stripe checkout in a new window
   * @param teamId - Team ID to subscribe
   * @param planId - Optional plan ID
   */
  const openPayment = async (teamId?: string, planId?: string) => {
    const checkoutUrl = await createCheckoutSession(teamId, planId);

    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  };

  /**
   * Opens Stripe checkout in the same window (for better mobile experience)
   * @param teamId - Team ID to subscribe
   * @param planId - Optional plan ID
   */
  const redirectToPayment = async (teamId?: string, planId?: string) => {
    const checkoutUrl = await createCheckoutSession(teamId, planId);

    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  return {
    openPayment,
    redirectToPayment,
    createCheckoutSession,
    isLoading,
    userEmail: user?.email
  };
};
